/**
 * Population Map with District Polygons
 *
 * Uses GeoJSON boundaries to show district areas colored by population density
 */

import { useEffect, useState } from 'react'
import { APIProvider, Map as GoogleMap, useMap } from '@vis.gl/react-google-maps'
import { AlertTriangle, Users, TrendingUp, Activity } from 'lucide-react'
import { MapErrorBoundary } from './MapErrorBoundary'
import { logger } from '@/shared/utils/logger'

interface DistrictPopulation {
  id: string
  name_th: string
  name_en: string
  density_level: 'low' | 'medium' | 'high'
  total_population: number
  population_density: number
  growth_rate: number
}

interface PopulationMapPolygonProps {
  districts?: DistrictPopulation[]
  className?: string
  year?: number
  selectedDistrictId?: number | null
  selectedDistrictName?: string | null
}

// Mock district population data - names must match GADM GeoJSON exactly
const MOCK_DISTRICTS: DistrictPopulation[] = [
  { id: '1', name_th: 'เมืองนครราชสีมา', name_en: 'MuangNakhonRatchasima', density_level: 'high', total_population: 461234, population_density: 645.2, growth_rate: 1.8 },
  { id: '2', name_th: 'ครบุรี', name_en: 'KhonBuri', density_level: 'medium', total_population: 89456, population_density: 142.3, growth_rate: 0.9 },
  { id: '3', name_th: 'เสิงสาง', name_en: 'SoengSang', density_level: 'low', total_population: 52341, population_density: 98.7, growth_rate: 0.5 },
  { id: '4', name_th: 'คง', name_en: 'Khong', density_level: 'low', total_population: 43678, population_density: 87.2, growth_rate: 0.3 },
  { id: '5', name_th: 'บ้านเหลื่อม', name_en: 'BanLuam', density_level: 'low', total_population: 61234, population_density: 105.4, growth_rate: 0.7 },
  { id: '6', name_th: 'จักราช', name_en: 'Chakkarat', density_level: 'medium', total_population: 78901, population_density: 132.8, growth_rate: 0.8 },
  { id: '7', name_th: 'โชคชัย', name_en: 'ChokChai', density_level: 'medium', total_population: 95432, population_density: 156.9, growth_rate: 1.1 },
  { id: '8', name_th: 'ด่านขุนทด', name_en: 'DanKhunThot', density_level: 'high', total_population: 123876, population_density: 198.4, growth_rate: 1.3 },
  { id: '9', name_th: 'โนนไทย', name_en: 'NonThai', density_level: 'low', total_population: 48765, population_density: 92.3, growth_rate: 0.4 },
  { id: '10', name_th: 'โนนสูง', name_en: 'NonSung', density_level: 'medium', total_population: 71298, population_density: 125.7, growth_rate: 0.9 },
  { id: '11', name_th: 'ขามสะแกแสง', name_en: 'KhamSakaeSaeng', density_level: 'low', total_population: 54987, population_density: 101.2, growth_rate: 0.6 },
  { id: '12', name_th: 'บัวใหญ่', name_en: 'BuaYai', density_level: 'medium', total_population: 82543, population_density: 138.9, growth_rate: 1.0 },
  { id: '13', name_th: 'ประทาย', name_en: 'PraThai', density_level: 'low', total_population: 46234, population_density: 89.5, growth_rate: 0.4 },
  { id: '14', name_th: 'ปักธงชัย', name_en: 'PakThongChai', density_level: 'high', total_population: 145678, population_density: 234.6, growth_rate: 1.5 },
  { id: '15', name_th: 'พิมาย', name_en: 'Phimai', density_level: 'medium', total_population: 87654, population_density: 145.3, growth_rate: 1.0 },
  { id: '16', name_th: 'ห้วยแถลง', name_en: 'HuaiThalang', density_level: 'low', total_population: 58432, population_density: 103.7, growth_rate: 0.7 },
  { id: '17', name_th: 'ปากช่อง', name_en: 'PakChong', density_level: 'medium', total_population: 98765, population_density: 165.4, growth_rate: 1.2 },
  { id: '18', name_th: 'ชุมพวง', name_en: 'ChumPhuang', density_level: 'low', total_population: 51234, population_density: 96.8, growth_rate: 0.5 },
  { id: '19', name_th: 'สีคิ้ว', name_en: 'Sikhiu', density_level: 'low', total_population: 67543, population_density: 115.2, growth_rate: 0.8 },
  { id: '20', name_th: 'เฉลิมพระเกียรติ', name_en: 'Chalermphrakiet', density_level: 'low', total_population: 39876, population_density: 78.4, growth_rate: 0.3 },
  { id: '21', name_th: 'สูงเนิน', name_en: 'SungNoen', density_level: 'medium', total_population: 73456, population_density: 128.9, growth_rate: 0.9 },
  { id: '22', name_th: 'โนนแดง', name_en: 'NonDaeng', density_level: 'low', total_population: 44567, population_density: 86.3, growth_rate: 0.4 },
  { id: '23', name_th: 'หนองบุญมาก', name_en: 'NongBunnak', density_level: 'low', total_population: 41234, population_density: 82.7, growth_rate: 0.3 },
  { id: '24', name_th: 'ขามทะเลสอ', name_en: 'KhamThalaSo', density_level: 'low', total_population: 38765, population_density: 76.9, growth_rate: 0.2 },
  { id: '25', name_th: 'แก้งสนามนาง', name_en: 'KaengSanamNang', density_level: 'low', total_population: 35432, population_density: 71.5, growth_rate: 0.2 },
  { id: '26', name_th: 'วังน้ำเขียว', name_en: 'WangNumKhiaw', density_level: 'low', total_population: 32198, population_density: 65.4, growth_rate: 0.1 },
  { id: '27', name_th: 'เทพารักษ์', name_en: 'K.ThePhaRak', density_level: 'low', total_population: 28765, population_density: 59.8, growth_rate: 0.1 },
  { id: '28', name_th: 'เมืองยาง', name_en: 'K.MuangYang', density_level: 'low', total_population: 24567, population_density: 52.3, growth_rate: 0.0 },
  { id: '29', name_th: 'พระทองคำ', name_en: 'K.PhraThongKham', density_level: 'low', total_population: 21234, population_density: 47.6, growth_rate: -0.1 },
  { id: '30', name_th: 'ลำทะเมนชัย', name_en: 'K.LamThaMenChai', density_level: 'low', total_population: 18432, population_density: 42.1, growth_rate: -0.2 },
  { id: '31', name_th: 'บัวลาย', name_en: 'K.BuaLai', density_level: 'low', total_population: 15678, population_density: 38.5, growth_rate: -0.2 },
  { id: '32', name_th: 'สีดา', name_en: 'K.Sida', density_level: 'low', total_population: 12345, population_density: 32.7, growth_rate: -0.3 },
]

const getDensityColor = (level: 'low' | 'medium' | 'high', isHovered: boolean = false) => {
  const opacity = isHovered ? 0.85 : 0.65
  switch (level) {
    case 'high':
      return `rgba(99, 102, 241, ${opacity})` // Indigo 500 - ความหนาแน่นสูง
    case 'medium':
      return `rgba(59, 130, 246, ${opacity})` // Blue 500 - ความหนาแน่นกลาง
    case 'low':
      return `rgba(14, 165, 233, ${opacity})` // Sky 500 - ความหนาแน่นต่ำ
  }
}

const getDensityLabel = (level: 'low' | 'medium' | 'high') => {
  switch (level) {
    case 'high': return 'หนาแน่น'
    case 'medium': return 'ปานกลาง'
    case 'low': return 'เบาบาง'
  }
}

// Inner component that applies GeoJSON to the map
function MapDataLayer({
  districts,
  geoJsonData
}: {
  districts: DistrictPopulation[]
  geoJsonData: any
}) {
  const map = useMap()

  // Map GeoJSON district names to our data
  const getDistrictData = (districtNameEn: string): DistrictPopulation | undefined => {
    return districts.find(d => d.name_en === districtNameEn)
  }

  // Apply GeoJSON to map - removed hoveredDistrict from dependencies for performance
  useEffect(() => {
    if (!map || !geoJsonData) {
      logger.debug('⏳ Waiting for map or GeoJSON...', {
        hasMap: !!map,
        hasGeoJson: !!geoJsonData,
        mapType: map ? typeof map : 'undefined',
        geoJsonFeatures: geoJsonData ? geoJsonData.features?.length : 0
      })
      return
    }

    logger.debug('🗺️ Map is ready! Applying district polygons...', {
      mapInstance: map,
      districtPolygons: geoJsonData.features.length,
      filteredDistricts: districts.length
    })

    // Clear existing data layers
    map.data.forEach(feature => map.data.remove(feature))

    // Filter GeoJSON features if specific districts are selected
    const districtNamesEn = districts.map(d => d.name_en)
    const filteredGeoJson = districts.length < 32 ? {
      ...geoJsonData,
      features: geoJsonData.features.filter((f: any) =>
        districtNamesEn.includes(f.properties.district_name_en)
      )
    } : geoJsonData

    logger.debug('📍 Displaying districts:', {
      totalDistricts: geoJsonData.features.length,
      displayedDistricts: filteredGeoJson.features.length,
      selectedNames: districtNamesEn
    })

    // Add GeoJSON features (filtered or all)
    const addedFeatures = map.data.addGeoJson(filteredGeoJson)
    logger.debug('✅ Added', addedFeatures.length, 'district polygons to map')

    // Center map on filtered district if only one selected
    if (districts.length === 1 && filteredGeoJson.features.length > 0) {
      const bounds = new google.maps.LatLngBounds()
      filteredGeoJson.features.forEach((feature: any) => {
        if (feature.geometry.type === 'Polygon') {
          feature.geometry.coordinates[0].forEach((coord: number[]) => {
            bounds.extend({ lat: coord[1], lng: coord[0] })
          })
        } else if (feature.geometry.type === 'MultiPolygon') {
          feature.geometry.coordinates.forEach((polygon: number[][][]) => {
            polygon[0].forEach((coord: number[]) => {
              bounds.extend({ lat: coord[1], lng: coord[0] })
            })
          })
        }
      })
      map.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 })
      logger.debug('🎯 Centered map on selected district:', districts[0].name_th)
    }

    // Track currently hovered feature for performance
    let currentHoveredFeature: google.maps.Data.Feature | null = null

    // Style features based on district data
    let matchCount = 0
    const unmatchedDistricts: string[] = []

    // Initial styling function - faster because no hover state
    const getFeatureStyle = (feature: google.maps.Data.Feature, isHovered: boolean = false) => {
      const districtNameEn = String(feature.getProperty('district_name_en'))
      const districtData = getDistrictData(districtNameEn)
      const riskLevel = districtData?.density_level || 'low'

      if (districtData && !isHovered) {
        matchCount++
      } else if (!districtData && !unmatchedDistricts.includes(districtNameEn) && !isHovered) {
        unmatchedDistricts.push(districtNameEn)
      }

      return {
        fillColor: getDensityColor(riskLevel, isHovered),
        fillOpacity: isHovered ? 0.85 : 0.65,
        strokeColor: isHovered ? '#1e3a8a' : '#64748b', // Navy blue when hover, slate when normal
        strokeWeight: isHovered ? 3 : 1,
        strokeOpacity: isHovered ? 0.9 : 0.5,
      }
    }

    map.data.setStyle(feature => getFeatureStyle(feature, false))

    // Log matching results
    const districtNames = geoJsonData.features.map((f: any) => String(f.properties.district_name_en))
    const uniqueDistricts: string[] = Array.from(new Set(districtNames))
    const matchedDistricts = uniqueDistricts.filter(d => getDistrictData(d))
    logger.debug('📊 District matching:', matchedDistricts.length, '/', uniqueDistricts.length, 'districts matched')

    if (unmatchedDistricts.length > 0) {
      const uniqueUnmatched = [...new Set(unmatchedDistricts)]
      logger.warn('⚠️ Unmatched districts:', uniqueUnmatched.slice(0, 5).join(', ')
        + (uniqueUnmatched.length > 5 ? ` (+${uniqueUnmatched.length - 5} more)` : ''))
    }

    // Create InfoWindow once - reuse for performance
    const infoWindow = new google.maps.InfoWindow()

    // Hover effect - only update specific feature style
    const mouseoverListener = map.data.addListener('mouseover', (event: google.maps.Data.MouseEvent) => {
      const feature = event.feature
      const districtNameEn = String(feature.getProperty('district_name_en'))
      const districtNameTh = String(feature.getProperty('district_name_th'))
      const districtData = getDistrictData(districtNameEn)

      // Update only this feature's style - much faster!
      currentHoveredFeature = feature
      map.data.overrideStyle(feature, getFeatureStyle(feature, true))

      // Show info window
      if (districtData) {
        const content = `
          <div style="font-family: 'IBM Plex Sans Thai', sans-serif; padding: 8px; min-width: 200px;">
            <h3 style="font-weight: 600; font-size: 15px; margin-bottom: 8px; color: #1e3a8a;">${districtNameTh}</h3>
            <div style="font-size: 13px; line-height: 1.6;">
              <div style="margin-bottom: 4px;">
                <strong>ความหนาแน่น:</strong>
                <span style="color: ${districtData.density_level === 'high' ? '#6366f1' : districtData.density_level === 'medium' ? '#3b82f6' : '#0ea5e9'}; font-weight: 600;">
                  ${getDensityLabel(districtData.density_level)}
                </span>
              </div>
              <div style="margin-bottom: 4px;"><strong>ประชากรทั้งหมด:</strong> ${String(districtData.total_population.toLocaleString('th-TH'))} คน</div>
              <div style="margin-bottom: 4px;"><strong>ความหนาแน่น:</strong> ${String(districtData.population_density)} คน/ตร.กม.</div>
              <div><strong>อัตราการเติบโต:</strong> ${String(districtData.growth_rate > 0 ? '+' : '')}${String(districtData.growth_rate)}%</div>
            </div>
          </div>
        `
        infoWindow.setContent(content)
        infoWindow.setPosition(event.latLng)
        infoWindow.open(map)
      }
    })

    const mouseoutListener = map.data.addListener('mouseout', () => {
      // Reset style of previously hovered feature
      if (currentHoveredFeature) {
        map.data.revertStyle(currentHoveredFeature)
        currentHoveredFeature = null
      }
      infoWindow.close()
    })

    // Cleanup listeners
    return () => {
      google.maps.event.removeListener(mouseoverListener)
      google.maps.event.removeListener(mouseoutListener)
      infoWindow.close()
    }

  }, [map, geoJsonData, districts]) // Removed hoveredDistrict - performance boost!

  return null // This component doesn't render anything
}

function PopulationMapPolygonInner({
  districts = MOCK_DISTRICTS,
  className = '',
  selectedDistrictId = null,
  selectedDistrictName = null
}: PopulationMapPolygonProps) {
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || ''
  const [geoJsonData, setGeoJsonData] = useState<any>(null)

  // Load district-level GeoJSON data (32 districts from GADM)
  useEffect(() => {
    fetch('/geojson/nakhon_ratchasima_districts.json')
      .then(res => res.json())
      .then(data => {
        logger.debug('✅ District GeoJSON loaded:', data.features.length, 'districts')
        setGeoJsonData(data)
      })
      .catch(err => logger.error('❌ Failed to load GeoJSON:', err))
  }, [])

  // Filter districts by name (not ID) to avoid mismatch between API and mock data
  const displayDistricts = selectedDistrictName
    ? districts.filter(d => {
        const match = d.name_th === selectedDistrictName
        if (match) {
          logger.debug('🎯 Filter matched by name:', {
            selectedDistrictName,
            districtId: d.id,
            districtName: d.name_th,
            districtNameEn: d.name_en
          })
        }
        return match
      })
    : districts

  logger.debug('🔍 Current filter state:', {
    selectedDistrictId,
    selectedDistrictName,
    totalDistricts: districts.length,
    displayedDistricts: displayDistricts.length,
    displayedNames: displayDistricts.map(d => d.name_th)
  })

  if (!apiKey) {
    return (
      <div className={`bg-amber-50 border border-amber-200 rounded-lg p-6 ${className}`}>
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-semibold text-amber-800">ยังไม่ได้ตั้งค่า Google Maps API Key</h3>
            <p className="text-sm text-amber-700 mt-1">
              กรุณาเพิ่ม <code className="bg-amber-100 px-1 rounded">VITE_GOOGLE_MAPS_API_KEY</code> ในไฟล์ <code className="bg-amber-100 px-1 rounded">.env</code>
            </p>
          </div>
        </div>
      </div>
    )
  }

  const center = { lat: 15.0, lng: 102.1 }
  const zoom = selectedDistrictId ? 10 : 8.5

  return (
    <div className={className}>
      {/* Info: District-level polygons from GADM */}
      <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
        <p className="text-sm text-blue-800 leading-relaxed">
          <strong>📍 หมายเหตุ:</strong> แผนที่แสดงขอบเขต<strong>ระดับอำเภอ</strong> (32 อำเภอ จากข้อมูล GADM)
          {selectedDistrictName && ` | 🎯 กรองแสดง: ${selectedDistrictName}`}
          <br />
          <span className="text-xs">💡 Hover เหนืออำเภอเพื่อดูข้อมูลประชากร | ⚠️ ข้อมูล Mock รอ API จริง</span>
        </p>
      </div>

      <APIProvider apiKey={apiKey}>
        <div className="h-[500px] rounded-xl overflow-hidden border-2 border-slate-200 shadow-lg">
          <GoogleMap
            defaultCenter={center}
            defaultZoom={zoom}
            disableDefaultUI={false}
            gestureHandling="greedy"
          >
            {geoJsonData && (
              <MapDataLayer
                districts={displayDistricts}
                geoJsonData={geoJsonData}
              />
            )}
          </GoogleMap>
        </div>
      </APIProvider>

      {/* Legend */}
      <div className="mt-4 bg-white rounded-lg border border-slate-200 p-4">
        <h3 className="text-sm font-semibold text-korat-navy-700 mb-3">ความหนาแน่นประชากร</h3>
        <div className="grid grid-cols-3 gap-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded border-2 border-slate-300 shadow-sm" style={{ backgroundColor: 'rgba(14, 165, 233, 0.65)' }} />
            <div>
              <div className="text-xs font-medium text-slate-900">เบาบาง</div>
              <div className="text-xs text-slate-500">
                {displayDistricts.filter(d => d.density_level === 'low').length} อำเภอ
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded border-2 border-slate-300 shadow-sm" style={{ backgroundColor: 'rgba(59, 130, 246, 0.65)' }} />
            <div>
              <div className="text-xs font-medium text-slate-900">ปานกลาง</div>
              <div className="text-xs text-slate-500">
                {displayDistricts.filter(d => d.density_level === 'medium').length} อำเภอ
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded border-2 border-slate-300 shadow-sm" style={{ backgroundColor: 'rgba(99, 102, 241, 0.65)' }} />
            <div>
              <div className="text-xs font-medium text-slate-900">หนาแน่น</div>
              <div className="text-xs text-slate-500">
                {displayDistricts.filter(d => d.density_level === 'high').length} อำเภอ
              </div>
            </div>
          </div>
        </div>
        <p className="text-xs text-slate-500 mt-3">
          💡 Hover เหนือแผนที่เพื่อดูรายละเอียดแต่ละอำเภอ
        </p>
      </div>

      {/* Statistics */}
      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <div className="text-xs text-slate-600">ประชากรรวม</div>
              <div className="text-xl font-bold text-korat-navy-700 tabular-nums">
                {displayDistricts.reduce((sum, d) => sum + d.total_population, 0).toLocaleString('th-TH')}
              </div>
              <div className="text-xs text-slate-500">คน</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
              <Activity className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <div className="text-xs text-slate-600">ความหนาแน่นเฉลี่ย</div>
              <div className="text-xl font-bold text-korat-navy-700 tabular-nums">
                {(displayDistricts.reduce((sum, d) => sum + d.population_density, 0) / displayDistricts.length).toFixed(1)}
              </div>
              <div className="text-xs text-slate-500">คน/ตร.กม.</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <div className="text-xs text-slate-600">อัตราเติบโตเฉลี่ย</div>
              <div className="text-xl font-bold text-korat-navy-700 tabular-nums">
                {(displayDistricts.reduce((sum, d) => sum + d.growth_rate, 0) / displayDistricts.length).toFixed(2)}%
              </div>
              <div className="text-xs text-slate-500">ต่อปี</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Population Map with Error Boundary
 * Wraps PopulationMapPolygonInner with MapErrorBoundary to handle map errors
 */
export function PopulationMapPolygon(props: PopulationMapPolygonProps) {
  return (
    <MapErrorBoundary>
      <PopulationMapPolygonInner {...props} />
    </MapErrorBoundary>
  )
}
